export interface ChangelogEntry {
  version: string;
  date: string; // YYYY-MM-DD
  title: string;
  changes: {
    type: 'feature' | 'fix' | 'improvement';
    text: string;
  }[];
}

const changelog: ChangelogEntry[] = [
  {
    version: '2.1.0',
    date: '2026-09-28',
    title: 'Leave System, AndBook & a Brand-New UI',
    changes: [
      { type: 'feature', text: 'Leave System — apply for leave with live balance checks and holiday-aware date picking' },
      { type: 'feature', text: 'Leave balances for every leave type (left / used) on your dashboard' },
      { type: 'feature', text: 'Manager → HR leave approvals with a single pending-approvals stack' },
      { type: 'feature', text: 'My Leaves — full leave history and status tracking' },
      { type: 'feature', text: 'AndBook — book single beds, twin rooms and meeting rooms across AndPayments offices' },
      { type: 'feature', text: 'AndBook interactive 3D office explorer, booking calendar, invitees and Teams links' },
      { type: 'feature', text: 'AndBook approvals, cancellations, notifications and an all-bookings admin view' },
      { type: 'improvement', text: 'Redesigned launcher with favourites, folders and a launchpad view' },
      { type: 'improvement', text: 'Refreshed sidebar, topbar and dashboards on a consistent design system' },
      { type: 'improvement', text: 'Light and dark themes across the platform' },
      { type: 'improvement', text: 'New attendance charts, contribution-graph calendar and date range picker' },
      { type: 'improvement', text: 'Faster page loads and smoother, mobile-friendly layouts' },
    ],
  },
  {
    version: '2.0.0',
    date: '2026-06-22',
    title: 'AndHub Platform: AndTimes, AndPlay & a New Launcher',
    changes: [
      { type: 'feature', text: 'AndHub launcher home — a unified Platforms page to reach every app in one place' },
      { type: 'feature', text: 'AndTimes — internal fintech news and reading app' },
      { type: 'feature', text: 'AndPlay — daily games with a fintech Quiz, Wordle, and a leaderboard' },
      { type: 'feature', text: 'Pin your favorite apps on the launcher for quick access' },
      { type: 'feature', text: 'Customizable dashboard layouts' },
      { type: 'feature', text: 'Employee-facing org chart' },
      { type: 'feature', text: 'Guided platforms tour and intro video for new users' },
      { type: 'feature', text: 'Admin analytics dashboard with page views and usage insights' },
      { type: 'feature', text: 'Admin AndTimes content management' },
      { type: 'feature', text: 'Admin QR code generator' },
      { type: 'feature', text: 'Super Admin role' },
      { type: 'feature', text: 'Bulk user editor for faster admin management' },
      { type: 'improvement', text: 'Attendance app rebranded to AndOne and moved under the AndHub umbrella' },
      { type: 'improvement', text: 'Last-seen presence tracking across the platform' },
      { type: 'improvement', text: 'Home announcements now support automatic expiry' },
    ],
  },
  {
    version: '1.12.0',
    date: '2026-06-02',
    title: 'Projects, Team Dashboard, Weather & UI Overhaul',
    changes: [
      { type: 'feature', text: 'Projects module with admin management and employee view' },
      { type: 'feature', text: 'Team dashboard page with team-level attendance insights' },
      { type: 'feature', text: 'Weather overlay on the employee dashboard' },
      { type: 'feature', text: 'What\'s New page showing version history and changelog' },
      { type: 'feature', text: 'Employee-facing org chart page' },
      { type: 'feature', text: 'Flowing menu component for enhanced navigation' },
      { type: 'feature', text: 'Illustrations for announcements, birthdays, and holidays' },
      { type: 'feature', text: 'Lottie animations for confetti and success states' },
      { type: 'improvement', text: 'Admin dashboard expanded with per-employee detail views and location stats' },
      { type: 'improvement', text: 'Admin org chart page significantly enhanced with analytics' },
      { type: 'improvement', text: 'Mark attendance page reworked with better UX' },
      { type: 'improvement', text: 'Clock-out modal redesigned' },
      { type: 'improvement', text: 'Sidebar and topbar UI refinements' },
      { type: 'improvement', text: 'User management page now defaults to card view' },
      { type: 'improvement', text: 'Pending approvals stack improvements' },
      { type: 'improvement', text: 'Admin daily view with better location breakdown' },
      { type: 'fix', text: 'Department and location filters now work correctly in user management' },
      { type: 'fix', text: 'Changelog dates corrected to match actual release history' },
    ],
  },
  {
    version: '1.11.0',
    date: '2026-05-14',
    title: 'Pending Approvals, Optional Geolocation & New Logo',
    changes: [
      { type: 'feature', text: 'Pending approvals stack on dashboard' },
      { type: 'feature', text: 'Export column selection modal for downloads' },
      { type: 'feature', text: 'Pending requests modals for quick review' },
      { type: 'feature', text: 'Geolocation is now optional for attendance marking' },
      { type: 'feature', text: 'QR generator page for admin' },
      { type: 'improvement', text: 'New app logo and UI improvements' },
    ],
  },
  {
    version: '1.10.0',
    date: '2026-05-13',
    title: 'QR Generator, Attendance Stats & Safari Fix',
    changes: [
      { type: 'feature', text: 'Bizcard QR generator' },
      { type: 'feature', text: 'Attendance overview stats with sorting' },
      { type: 'fix', text: 'Safari geolocation compatibility fix' },
    ],
  },
  {
    version: '1.9.0',
    date: '2026-04-29',
    title: 'Notification Modal, Announcement TTL & Dashboard Fixes',
    changes: [
      { type: 'feature', text: 'Full message modal for notifications' },
      { type: 'feature', text: 'Announcement TTL/expiry support' },
      { type: 'fix', text: 'Dashboard default values and minsToTime overflow fix' },
    ],
  },
  {
    version: '1.8.0',
    date: '2026-04-20',
    title: 'Org Chart, Audit Log Redesign & Settings Overhaul',
    changes: [
      { type: 'feature', text: 'Full org chart page with analytics' },
      { type: 'improvement', text: 'Audit log page redesigned with better filtering' },
      { type: 'improvement', text: 'Admin settings page overhauled' },
      { type: 'improvement', text: 'Users page improvements and UI polish' },
    ],
  },
  {
    version: '1.7.0',
    date: '2026-04-21',
    title: 'Carousel, Profiles & Confetti',
    changes: [
      { type: 'feature', text: 'Improved home carousel with smoother transitions' },
      { type: 'feature', text: 'Profile completion warning now includes location check' },
      { type: 'improvement', text: 'Birthday window fixes and sidebar opacity adjustments' },
      { type: 'feature', text: 'Confetti animation on special occasions' },
      { type: 'fix', text: 'Clock heatmap shared component and early-bucket fixes' },
    ],
  },
  {
    version: '1.6.0',
    date: '2026-04-18',
    title: 'Role Editing & Admin Filters',
    changes: [
      { type: 'fix', text: 'Editing user roles no longer overwrites other user fields' },
      { type: 'fix', text: 'Admin dashboard filters now correctly exclude non-Employee roles' },
      { type: 'fix', text: 'Admin filters replaced invalid role column reference with proper JOIN query' },
    ],
  },
  {
    version: '1.5.0',
    date: '2026-04-18',
    title: 'Cross-Tab Auth & Token Sync',
    changes: [
      { type: 'fix', text: 'Token sync now works correctly across multiple browser tabs' },
      { type: 'fix', text: 'Fixed broken lock API in the HTTP client' },
      { type: 'fix', text: 'Auth refresh now runs independently per tab instead of skipping on broadcast' },
      { type: 'improvement', text: 'Added retried guard on blob requests' },
    ],
  },
  {
    version: '1.4.0',
    date: '2026-04-17',
    title: 'FAQ, Dark Mode & Attendance UX',
    changes: [
      { type: 'feature', text: 'Added comprehensive FAQ page with search and admin sections' },
      { type: 'improvement', text: 'Increased font sizes across the app for better readability' },
      { type: 'fix', text: 'Dark mode color fixes and consistency improvements' },
      { type: 'improvement', text: 'Attendance marking UX improvements' },
    ],
  },
  {
    version: '1.3.0',
    date: '2026-04-16',
    title: 'IST Clock Times, Multi-Passkey & Announcements',
    changes: [
      { type: 'feature', text: 'Admin attendance now displays clock times in IST' },
      { type: 'feature', text: 'Announcement sender identification in notifications' },
      { type: 'feature', text: 'Support for multiple passkeys per user' },
      { type: 'improvement', text: 'Auth flow improvements and stability fixes' },
    ],
  },
  {
    version: '1.2.0',
    date: '2026-04-16',
    title: 'Onboarding Tour, Passkey Providers & Excel Import',
    changes: [
      { type: 'feature', text: 'Interactive onboarding tour for new users using Driver.js' },
      { type: 'feature', text: 'Passkey provider identification (AAGUID lookup)' },
      { type: 'feature', text: 'Excel import with per-row configuration' },
      { type: 'fix', text: 'Various bug fixes and stability improvements' },
    ],
  },
  {
    version: '1.1.0',
    date: '2026-04-16',
    title: 'RBAC, Redis Cache & Rate Limiting',
    changes: [
      { type: 'feature', text: 'Role-based access control with granular permissions' },
      { type: 'feature', text: 'Redis-backed server-side caching for faster responses' },
      { type: 'feature', text: 'API rate limiting to prevent abuse' },
      { type: 'improvement', text: 'UI polish and visual refinements across all pages' },
    ],
  },
  {
    version: '1.0.0',
    date: '2026-04-16',
    title: 'Admin Dashboard & Profile Photos',
    changes: [
      { type: 'feature', text: 'Admin dashboard with daily/monthly attendance trend charts' },
      { type: 'feature', text: 'Predefined date range presets for attendance history' },
      { type: 'feature', text: 'Profile photos cached in auth context' },
      { type: 'improvement', text: 'Clock time refactor with date mismatch detection' },
      { type: 'feature', text: 'Vercel Speed Insights integration' },
    ],
  },
  {
    version: '0.1.0',
    date: '2026-04-15',
    title: 'Initial Release',
    changes: [
      { type: 'feature', text: 'Microsoft SSO login with Azure AD' },
      { type: 'feature', text: 'Employee dashboard with clock-in/out via WebAuthn' },
      { type: 'feature', text: 'Attendance records, calendar, and request management' },
      { type: 'feature', text: 'Admin panel for users, departments, locations, and holidays' },
      { type: 'feature', text: 'Org chart, audit log, and email log' },
      { type: 'feature', text: 'Notification system with in-app and email alerts' },
    ],
  },
];

export const APP_VERSION = changelog[0].version;

export default changelog;
